import { useRef } from 'react';

import { getPlaceholderHtml as getNumberPlaceholderHtml } from '@components/field-mapping-number-field/extensions';
import { Form } from '@components/form';
import { FormValidatorType } from '@components/form/types';
import { IconAddPlus } from '@components/icons/add-plus';
import { PopoverButton } from '@components/popover-button';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { formatNumber } from '@utils/numbers';
import { uuid } from '@utils/uuid';

import { EditorRefProp } from '../common';
import type { NumberManualRestrictions } from '../field-mapping-sidebar';

type NumberManualFieldProps = EditorRefProp & {
  isDecimal?: boolean;
  numberRestrictions?: NumberManualRestrictions;
};

export const NumberManualField = ({
  editorRef,
  isDecimal,
  numberRestrictions,
}: NumberManualFieldProps) => {
  const { t } = useScopedTranslate(numberLocale);
  const fieldId = useRef(uuid()).current;
  const validators: FormValidatorType[] = numberRestrictions
    ? [
        (value: unknown) => {
          const num = typeof value === 'number' ? value : Number(value);
          if (!Number.isFinite(num)) return true;
          const { min, max, disableNegative } = numberRestrictions;
          if (disableNegative && num < 0) return t('errorNoNegative');
          if (min != null && num < min) return t('errorMin', { min: formatNumber(min) });
          if (max != null && num > max) return t('errorMax', { max: formatNumber(max) });
          return true;
        },
      ]
    : [];
  return (
    <PopoverButton
      placement="bottom-start"
      triggerProps={{
        iconLeft: <IconAddPlus />,
        variant: 'secondary',
        text: t('addNumber'),
      }}
      contentStyle={{ maxWidth: pxToRem(392) }}
    >
      {({ close }) => (
        <Form
          onSubmit={(values) => {
            const value = values[fieldId];
            const number = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(number)) return;
            const { min, max, disableNegative } = numberRestrictions ?? {};
            if (disableNegative && number < 0) return;
            if (min != null && number < min) return;
            if (max != null && number > max) return;
            const label = formatNumber(number);
            editorRef.current?.insertContent(getNumberPlaceholderHtml({ value, label }));
            close();
          }}
        >
          <Form.NumberField
            id={fieldId}
            label={t('numberLabel')}
            isDecimal={isDecimal}
            autoFocus
            validators={validators}
          />
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} onClick={() => close()} />
            <Form.Submit variant="primary-blue" text={t('core.add')} iconLeft={<IconAddPlus />} />
          </Form.Actions>
        </Form>
      )}
    </PopoverButton>
  );
};

const numberLocale = {
  en: {
    addNumber: 'Add number',
    numberLabel: 'Number',
    errorNoNegative: 'Negative numbers not allowed',
    errorMin: 'Minimum is {min}',
    errorMax: 'Maximum is {max}',
  },
};
